/**
 * @file index.tsx
 *
 * @fileoverview Renders the steps for the job wizard.
 * Automatically detects if the text label should be selected based on current path.
 */
import React, { useContext } from 'react';
import { JobWizardFlow, StepKeys } from '~/jobiqo/components/Job/Wizard/config';
import { useRuntimeStepsInfo } from '~/jobiqo/components/Job/Wizard/hooks/useRuntimeStepsInfo';
import WizardStepsWrapper from '~/jobiqo/components/Wizard/StepsWrapper';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { WizardStepSimple } from '~/vcl/atoms/23-wizard/01-step-simple';
import { WizardStepSimpleConnector } from '~/vcl/atoms/23-wizard/02-simple-step-connector';
import { WizardStepSimpleList } from '~/vcl/molecules/12-wizard/02-simple-step-list';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface JobWizardStepsProps {
  /**
   * If the wizard is in republishing mode.
   *
   * @default "new"
   */
  flow?: JobWizardFlow;
}
export const JobWizardSteps = ({
  flow = JobWizardFlow.New,
}: JobWizardStepsProps) => {
  const pathContext: PathContextInterface = useContext(PathContext);
  const { JOBS } = useFrontendConfig();
  const ALLOWED_JOB_TYPES = JOBS.ALLOWED_JOB_TYPES;
  const { stepsInfo } = useRuntimeStepsInfo(flow);

  // If there is only one allowed job type, we skip the job type step in the wizard steps.
  const filteredStepsInfo =
    ALLOWED_JOB_TYPES.length === 1
      ? stepsInfo.filter((step) => step.key !== StepKeys.type)
      : stepsInfo;

  // Check if the current step is the last based on path.
  const currentStepNumber = filteredStepsInfo.findIndex(
    (step) => step.page === pathContext.pathname,
  );

  return (
    <WizardStepsWrapper totalSteps={filteredStepsInfo.length}>
      <WizardStepSimpleList>
        {filteredStepsInfo.map((stepInfo, index) => {
          return (
            <React.Fragment key={index}>
              <WizardStepSimple
                key={index}
                text={stepInfo.title}
                selected={currentStepNumber >= index}
                checked={currentStepNumber > index}
                index={index}
                current={index === currentStepNumber}
              />
              {index < filteredStepsInfo.length - 1 ? (
                <WizardStepSimpleConnector />
              ) : null}
            </React.Fragment>
          );
        })}
      </WizardStepSimpleList>
    </WizardStepsWrapper>
  );
};
export default JobWizardSteps;
